import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground shadow-xs hover:opacity-90',
				destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:opacity-90',
				outline: 'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:opacity-80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-md px-3 text-xs',
				lg: 'h-10 rounded-md px-6',
				icon: 'h-9 w-9',
			},
		},
		defaultVariants: { variant: 'default', size: 'default' },
	},
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	loading?: boolean;
}

function Button({ className, variant, size, asChild = false, loading, disabled, children, ...props }: ButtonProps) {
	if (asChild) {
		return (
			<Slot className={cn(buttonVariants({ variant, size, className }))} {...props}>
				{children}
			</Slot>
		);
	}
	return (
		<button className={cn(buttonVariants({ variant, size, className }))} disabled={disabled || loading} {...props}>
			{loading ? <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : null}
			{children}
		</button>
	);
}

export { Button, buttonVariants };
